// src/components/layout/Navbar.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLocale } from "../../contexts/LocaleContext";
import MenuItem from "./MenuItem";
import SearchModal from "../search/SearchModal";

function Navbar() {
  const {
    locale,
    changeLocale,
    headerData,
    isLoadingSharedData,
    sharedDataError,
  } = useLocale();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedMobileSubMenus, setExpandedMobileSubMenus] = useState({});
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const openSearchModal = () => {
    setIsMobileMenuOpen(false);
    setIsSearchModalOpen(true);
  };

  const closeSearchModal = () => {
    setIsSearchModalOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileMenuOpen) {
      setExpandedMobileSubMenus({});
    }
  };

  const closeMobileMenuOnNavigate = () => {
    setIsMobileMenuOpen(false);
    setExpandedMobileSubMenus({});
  };

  const handleMobileSubMenuToggle = (itemId) => {
    setExpandedMobileSubMenus((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const renderMobileMenuItems = (items, level = 0) => {
    if (!items || items.length === 0) return null;

    return (
      <ul className={level > 0 ? "pl-4 mt-1 space-y-1" : "space-y-1"}>
        {items.map((item) => {
          const hasSubItems =
            item.sub_menu_items && item.sub_menu_items.length > 0;
          const isExpanded = !!expandedMobileSubMenus[item.id];

          return (
            <li key={item.id || item.title}>
              {item.url && !hasSubItems ? (
                <Link
                  to={item.url}
                  className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  onClick={closeMobileMenuOnNavigate}
                >
                  {item.title}
                </Link>
              ) : (
                <button
                  type="button"
                  className="w-full flex items-center justify-between text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none"
                  onClick={() => {
                    if (hasSubItems) {
                      handleMobileSubMenuToggle(item.id);
                    }
                  }}
                >
                  <span>{item.title}</span>
                  {hasSubItems && (
                    <svg
                      className={`w-5 h-5 transform transition-transform duration-150 ${
                        isExpanded ? "rotate-180" : "rotate-0"
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              )}
              {hasSubItems && isExpanded && (
                <div className="mt-1">
                  {renderMobileMenuItems(item.sub_menu_items, level + 1)}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const renderLogo = () => {
    if (isLoadingSharedData && (!headerData || !headerData.logoData)) {
      return (
        <div className="h-10 w-32 bg-gray-200 animate-pulse rounded"></div>
      );
    }
    if (headerData && headerData.logoData && headerData.logoData.url) {
      const logoSrc = `${headerData.logoData.url}`;
      return (
        <img
          src={logoSrc}
          alt={headerData.logoData.alternativeText || "Aqutek Logo"}
          className="h-9 md:h-10 w-auto"
        />
      );
    }
    return (
      <span className="font-bold text-2xl md:text-3xl text-[#00529C]">
        AQUTEK
      </span>
    );
  };

  const enButtonClass = `text-xs font-medium px-2.5 py-[5px] rounded-[3px] focus:outline-none shadow-sm transition-colors ${
    locale === "en"
      ? "text-white bg-[#004A99] hover:bg-[#003D80]"
      : "text-gray-600 bg-white hover:bg-gray-100 hover:text-blue-500 border border-gray-300"
  }`;

  const zhButtonClass = `ml-1.5 text-xs font-medium px-2.5 py-[5px] rounded-[3px] focus:outline-none shadow-sm transition-colors ${
    locale === "zh-Hant"
      ? "text-white bg-[#004A99] hover:bg-[#003D80]"
      : "text-gray-600 bg-white hover:bg-gray-100 hover:text-blue-500 border border-gray-300"
  }`;

  return (
    <>
      <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-[31]">
        <div className="container mx-auto px-2 sm:px-4 xl:px-6">
          <div className="flex items-center justify-between h-18 md:h-20">
            <Link
              to="/"
              className="flex-shrink-0"
              onClick={closeMobileMenuOnNavigate}
            >
              {renderLogo()}
            </Link>

            {/* Desktop Menu */}
            <div className="hidden xl:flex flex-grow justify-start pl-4 xl:pl-8">
              <ul className="flex items-center space-x-2 xl:space-x-4">
                {isLoadingSharedData && headerData.menuItems.length === 0 && (
                  <li className="px-3 py-2 text-sm text-gray-400">
                    Loading...
                  </li>
                )}

                {sharedDataError && (
                  <li className="px-3 py-2 text-sm text-red-500">
                    {sharedDataError}
                  </li>
                )}
                {!isLoadingSharedData &&
                  !sharedDataError &&
                  headerData.menuItems.map((item) => (
                    <MenuItem key={item.id || item.title} item={item} />
                  ))}
              </ul>
            </div>

            <div className="hidden xl:flex items-center space-x-3 xl:space-x-4">
              <button
                type="button"
                aria-label="Search"
                className="text-gray-600 hover:text-blue-500 p-1.5 rounded-full focus:outline-none"
                onClick={openSearchModal}
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </button>
              <div className="flex items-center">
                <button
                  type="button"
                  className={enButtonClass}
                  onClick={() => changeLocale("en")}
                >
                  EN
                </button>
                <button
                  type="button"
                  className={zhButtonClass}
                  onClick={() => changeLocale("zh-Hant")}
                >
                  繁
                </button>
              </div>
              <a
                className="bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-blue-700 transition-colors flex items-center shadow-md"
                href={headerData.onlineShopTextUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg
                  className="inline-block h-4 w-4 mr-2 -mt-px"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                </svg>
                {headerData.onlineShopText}
              </a>
            </div>

            {/* Mobile menu button */}
            <div className="flex xl:hidden space-x-3">
              <div className="flex flex-grow items-center space-x-3 xl:space-x-4">
                <button
                  type="button"
                  aria-label="Search"
                  className="text-gray-600 hover:text-blue-500 p-1.5 rounded-full focus:outline-none"
                  onClick={openSearchModal}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </button>
                <div className="flex items-center">
                  <button
                    type="button"
                    className={enButtonClass}
                    onClick={() => changeLocale("en")}
                  >
                    EN
                  </button>
                  <button
                    type="button"
                    className={zhButtonClass}
                    onClick={() => changeLocale("zh-Hant")}
                  >
                    繁
                  </button>
                </div>
                <a
                  className="bg-blue-600 text-white px-2 md:px-4 py-2 rounded text-sm font-medium hover:bg-blue-700 transition-colors flex items-center shadow-md"
                  href={headerData.onlineShopTextUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    className="inline-block h-4 w-4 md:mr-2 -mt-px"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                  </svg>
                  <div className="hidden md:flex">
                    {headerData.onlineShopText}
                  </div>
                </a>
              </div>
              <button
                onClick={toggleMobileMenu}
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                aria-controls="mobile-menu"
                aria-expanded={isMobileMenuOpen ? "true" : "false"}
              >
                <span className="sr-only">Open main menu</span>
                {isMobileMenuOpen ? (
                  <svg
                    className="block h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    className="block h-6 w-6"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div
            className="xl:hidden absolute top-full left-0 right-0 bg-white shadow-lg z-[30] max-h-[calc(100vh-5rem)] overflow-y-auto"
            id="mobile-menu"
          >
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {isLoadingSharedData && headerData.menuItems.length === 0 && (
                <div className="block px-3 py-2 rounded-md text-base font-medium text-gray-500">
                  Loading...
                </div>
              )}
              {sharedDataError && (
                <div className="block px-3 py-2 rounded-md text-base font-medium text-red-500">
                  Error:{" "}
                  {typeof sharedDataError === "string"
                    ? sharedDataError
                    : "Failed to load menu"}
                </div>
              )}
              {!isLoadingSharedData &&
                !sharedDataError &&
                headerData.menuItems &&
                headerData.menuItems.length > 0 &&
                renderMobileMenuItems(headerData.menuItems)}
              {!isLoadingSharedData &&
                !sharedDataError &&
                (!headerData.menuItems ||
                  headerData.menuItems.length === 0) && (
                  <div className="block px-3 py-2 rounded-md text-base font-medium text-gray-500">
                    No menu items available.
                  </div>
                )}
            </div>
          </div>
        )}
      </nav>
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={closeSearchModal}
      />
    </>
  );
}

export default Navbar;
